import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

const assistantWidth = 360;
const gap = 16;

// Use the workspace's available width, including the space taken by app navigation.
export function sophieWorkspaceLayout(width, open, { preferFilesSidebar = false, filesWidth = 280 } = {}) {
  const docked = open && width >= 856;
  const workspaceWidth = width - (docked ? assistantWidth + gap : 0);
  // In Write, reserve a usable editor before falling back to Files/Workspace tabs.
  const sidebarSpace = filesWidth > 0 ? filesWidth + gap : 0;
  const collapseFiles = open && workspaceWidth < (preferFilesSidebar ? sidebarSpace + 480 : 1160);
  const editorWidth = workspaceWidth - (preferFilesSidebar && !collapseFiles ? sidebarSpace : 0);
  return {
    docked,
    workspaceWidth,
    collapseFiles,
    compactEditors: open && editorWidth < 760,
  };
}

export default function SophieWorkspaceLayout({ open, assistant, children, workspaceLabel = 'Workspace', preferFilesSidebar = false, filesWidth = 280 }) {
  const rootRef = useRef(null);
  const assistantRef = useRef(null);
  const wasOpen = useRef(false);
  const [width, setWidth] = useState(() => window.innerWidth);
  const [height, setHeight] = useState(600);
  const [activePane, setActivePane] = useState('assistant');
  const layout = sophieWorkspaceLayout(width, open, { preferFilesSidebar, filesWidth });

  useLayoutEffect(() => {
    const measure = () => {
      const nextWidth = rootRef.current?.getBoundingClientRect().width;
      if (nextWidth) setWidth(nextWidth);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (rootRef.current) observer?.observe(rootRef.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const fit = () => {
      // Measure the workspace's natural position rather than the already-sticky
      // panel. As the page scrolls, grow to the viewport and then stay pinned.
      const anchor = layout.docked ? rootRef.current : assistantRef.current;
      const top = Math.max(16, anchor?.getBoundingClientRect().top ?? 16);
      setHeight(Math.max(0, window.innerHeight - top - 16));
    };
    fit();
    window.addEventListener('resize', fit);
    document.addEventListener('scroll', fit, true);
    return () => { window.removeEventListener('resize', fit); document.removeEventListener('scroll', fit, true); };
  }, [open, layout.docked, activePane]);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setActivePane('assistant');
      if (!layout.docked) rootRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
    wasOpen.current = open;
  }, [open, layout.docked]);

  return <Box ref={rootRef} data-sophie-layout={open ? layout.docked ? 'docked' : 'tabs' : 'closed'} sx={{ minWidth: 0, scrollMarginTop: 16 }}>
    {open && !layout.docked && <Tabs value={activePane} onChange={(_, value) => setActivePane(value)} aria-label="Workspace and Sophie" sx={{ mb: 2 }}>
      <Tab id="sophie-workspace-tab" aria-controls="sophie-workspace" value="workspace" label={workspaceLabel} />
      <Tab id="sophie-assistant-tab" aria-controls="sophie-assistant" value="assistant" label="Sophie" />
    </Tabs>}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
      {/* Keep the editor subtree mounted when changing layout or tabs. */}
      <Box id="sophie-workspace" role="region" aria-label="Consultation workspace" hidden={open && !layout.docked && activePane !== 'workspace'}
        sx={{ flex: 1, minWidth: 0, display: open && !layout.docked && activePane !== 'workspace' ? 'none' : 'block' }}>
        {children(layout)}
      </Box>
      {open && <Box id="sophie-assistant" ref={assistantRef} hidden={!layout.docked && activePane !== 'assistant'}
        sx={{ width: layout.docked ? assistantWidth : '100%', flexShrink: 0, minWidth: 0, minHeight: 0, height,
          position: layout.docked ? 'sticky' : 'relative', top: layout.docked ? 16 : undefined,
          display: !layout.docked && activePane !== 'assistant' ? 'none' : 'block' }}>
        {assistant}
      </Box>}
    </Box>
  </Box>;
}
